import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SortIcon from "@mui/icons-material/Sort";
import { Box, Button, Typography } from "@mui/material";

const CommonTable = ({
  tableData = [],
  columns = [],
  currentTimestamp,
  showFilter = false,
  filterOptions = [],
  legendColors = {},
  searchableKeys = [],
  tableName = "Data",
  searchPlaceholder = "Search...",
  emptyMessage = "No data found.",
  highlightRowKey = null,
  rowIdKey = null,
  onRowClick = null,
  scrollableBody = false,
  defaultSort = null,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState(
    defaultSort || { key: null, direction: null }
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [activeFilter, setActiveFilter] = useState("All");
  const [visibleColumns, setVisibleColumns] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  );

  // ---------------- SORT (3-STATE) ----------------
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      if (prev.direction === "desc") return { key: null, direction: null };
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key || !sortConfig.direction) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // ---------------- FILTER + SEARCH + SORT ----------------
  const filteredData = useMemo(() => {
    let data = [...tableData];

    if (showFilter && activeFilter !== "All") {
      data = data.filter((item) => {
        const adv = (item.advisory || "N/A").toLowerCase();
        const f = activeFilter.toLowerCase();
        if (f === "norisk") return adv.includes("no risk") || adv.includes("norisk");
        return adv.includes(f);
      });
    }

    const searchLower = searchTerm.toLowerCase();
    data = data.filter((row) =>
      searchableKeys.some((key) =>
        String(row[key] ?? "").toLowerCase().includes(searchLower)
      )
    );

    if (sortConfig.key && sortConfig.direction) {
      // A column may define sortValue(row) to sort by something other than its
      // displayed text (e.g. risk level order). Values must be non-zero numbers
      // or strings, since falsy values are treated as empty below.
      const sortCol = columns.find((c) => c.key === sortConfig.key);
      data.sort((a, b) => {
        let aVal = (sortCol?.sortValue ? sortCol.sortValue(a) : a[sortConfig.key]) || "";
        let bVal = (sortCol?.sortValue ? sortCol.sortValue(b) : b[sortConfig.key]) || "";

        if (sortConfig.key === "date_hr") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (aVal !== "" && bVal !== "" && !isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
        }

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [tableData, activeFilter, searchTerm, sortConfig, searchableKeys, showFilter, columns]);

  // ---------------- PAGINATION ----------------
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Reset to page 1 when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, rowsPerPage, searchTerm]);

  // ---------------- HIGHLIGHT / AUTO-PAGE TO A ROW ----------------
  const highlightedRowRef = useRef(null);

  useEffect(() => {
    if (!rowIdKey || highlightRowKey === null || highlightRowKey === undefined) return;
    const index = filteredData.findIndex((row) => row[rowIdKey] === highlightRowKey);
    if (index === -1) return;
    const targetPage = Math.floor(index / rowsPerPage) + 1;
    if (targetPage !== currentPage) setCurrentPage(targetPage);
  }, [highlightRowKey, rowIdKey, filteredData, rowsPerPage, currentPage]);

  useEffect(() => {
    if (highlightRowKey === null || highlightRowKey === undefined) return;
    highlightedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightRowKey, currentPage]);

  // ---------------- COLORS ----------------
  const getColor = (advisory) => {
    const adv = (advisory || "").toLowerCase();
    if (adv.includes("high")) return legendColors.High || "rgb(230, 45, 58)";
    if (adv.includes("medium") || adv.includes("moderate")) return legendColors.Medium || "rgb(243, 155, 58)";
    if (adv.includes("low")) return legendColors.Low || "rgb(243, 235, 18)";
    if (adv.includes("norisk") || adv.includes("no risk")) return legendColors.NoRisk || "rgb(148, 203, 85)";
    return legendColors["N/A"] || "rgb(222,222,223)";
  };

  // ---------------- EXPORT ----------------
  const exportToCSV = useCallback(() => {
    if (!filteredData.length) return;

    const visibleCols = columns.filter(c => visibleColumns[c.key]);
    const headers = visibleCols.map((c) => c.label);

    const csv = [
      headers.join(","),
      ...filteredData.map((row, i) =>
        visibleCols
          .map((c) => {
            if (c.key === "SlNo") return i + 1;
            let val = row[c.key] || "";
            if (typeof val === "string") val = `"${val.replace(/"/g, '""')}"`;
            return val;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${tableName}_${currentTimestamp || "data"}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [filteredData, columns, currentTimestamp, tableName, visibleColumns]);

  // ---------------- FILTER BUTTONS ----------------
  const filterButtons = useMemo(() => {
    return filterOptions.map((level) => (
      <Button
        key={level}
        onClick={() => setActiveFilter(level)}
        sx={{
          minWidth: "95px",
          padding: "8px 14px",
          borderRadius: "10px",
          fontWeight: "bold",
          fontSize: "14px",
          textTransform: "none",
          backgroundColor: legendColors[level] || "#f0f0f0",
          color: level === "High" || level === "Medium" ? "white" : "black",
          border: activeFilter === level ? "2px solid #333" : "2px solid transparent",
          "&:hover": {
            opacity: 0.9,
            transform: "scale(1.02)",
          },
          transition: "all 0.2s ease",
        }}
      >
        {level === "NoRisk" ? "No Risk" : level}
      </Button>
    ));
  }, [activeFilter, legendColors, filterOptions]);

  const visibleColumnsArray = columns.filter(c => visibleColumns[c.key]);

  return (
    <div className="info-content-text" style={scrollableBody ? { height: "100%" } : undefined}>
      <Box
        sx={{
          p: 0,
          width: "100%",
          background: "#f5f7fa",
          ...(scrollableBody && {
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }),
        }}
      >
        {/* FILTER BAR */}
        {showFilter && (
          <Box
            sx={{
              mx: 2,
              mb: 2,
              mt: 1,
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <SortIcon sx={{ color: "#2563eb", fontSize: 28 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#333" }}>
              Filter By Risk Level:
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flex: 1 }}>
              {filterButtons}
            </Box>
          </Box>
        )}

        {/* CONTROLS BAR */}
        <div
          style={{
            margin: "0 16px 12px",
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            padding: "12px",
            background: "#f8fafc",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            ...(scrollableBody && { flexShrink: 0 }),
          }}
        >
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              fontSize: "13px",
              flex: "1 1 280px",
              minWidth: "280px",
            }}
          />

          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{
              padding: "8px 12px",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              fontSize: "13px",
              background: "white",
            }}
          >
            <option value={15}>15 rows</option>
            <option value={25}>25 rows</option>
            <option value={50}>50 rows</option>
            <option value={filteredData.length}>All ({filteredData.length} rows)</option>
          </select>

          <button
            onClick={exportToCSV}
            disabled={filteredData.length === 0}
            style={{
              padding: "8px 16px",
              background: "#0d6efd",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: filteredData.length === 0 ? "not-allowed" : "pointer",
              opacity: filteredData.length === 0 ? 0.6 : 1,
            }}
          >
            <FileDownloadIcon sx={{ mr: 1, fontSize: 18 }} />
            Export CSV
          </button>

          {/* Column Visibility Toggle */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                const dropdown = document.getElementById("column-dropdown");
                dropdown.style.display = dropdown.style.display === "block" ? "none" : "block";
              }}
              style={{
                padding: "8px 16px",
                background: "#64748b",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Show/Hide Columns
            </button>
            <div
              id="column-dropdown"
              style={{
                display: "none",
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "4px",
                background: "white",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                padding: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                zIndex: 1000,
                minWidth: "200px",
              }}
            >
              {columns.map((col) => (
                <label
                  key={col.key}
                  style={{
                    display: "block",
                    padding: "4px 8px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns[col.key]}
                    onChange={(e) =>
                      setVisibleColumns({ ...visibleColumns, [col.key]: e.target.checked })
                    }
                    style={{ marginRight: "8px" }}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div
          style={{
            margin: "0 16px 16px",
            overflowX: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            background: "white",
            ...(scrollableBody && { flex: 1, minHeight: 0, overflowY: "auto" }),
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: scrollableBody ? "separate" : "collapse",
              borderSpacing: scrollableBody ? 0 : undefined,
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                {visibleColumnsArray.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && handleSort(col.key)}
                    style={{
                      border: "1px solid #e2e8f0",
                      padding: "12px 10px",
                      textAlign: col.align || "left",
                      fontWeight: 700,
                      cursor: col.sortable ? "pointer" : "default",
                      userSelect: "none",
                      ...(scrollableBody && {
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        background: "#f1f5f9",
                      }),
                    }}
                  >
                    {col.label} {col.sortable && getSortIcon(col.key)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumnsArray.length}
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => {
                  const isHighlighted = rowIdKey && row[rowIdKey] === highlightRowKey;
                  return (
                    <tr
                      key={idx}
                      ref={isHighlighted ? highlightedRowRef : null}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        backgroundColor: isHighlighted ? "#fef3c7" : undefined,
                        cursor: onRowClick ? "pointer" : "default",
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      {visibleColumnsArray.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            border: "1px solid #e2e8f0",
                            padding: "10px",
                            textAlign: col.align || "left",
                            ...(col.cellStyle ? col.cellStyle(row) : null),
                          }}
                        >
                          {col.key === "SlNo"
                            ? (currentPage - 1) * rowsPerPage + idx + 1
                            : col.render
                            ? col.render(row, getColor)
                            : row[col.key] || "-"}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION - ENHANCED STYLING */}
        {totalPages > 1 && (
          <div
            style={{
              margin: "0 16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 12px",
              background: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              ...(scrollableBody && { flexShrink: 0 }),
            }}
          >
            <div style={{ fontSize: "13px", color: "#64748b" }}>
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredData.length)} of{" "}
              {filteredData.length} entries
            </div>

            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  background: currentPage === 1 ? "#f1f5f9" : "white",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: currentPage === 1 ? "#94a3b8" : "#334155",
                  transition: "all 0.2s ease",
                }}
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  background: currentPage === 1 ? "#f1f5f9" : "white",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: currentPage === 1 ? "#94a3b8" : "#334155",
                  transition: "all 0.2s ease",
                }}
              >
                Previous
              </button>
              <span
                style={{
                  padding: "6px 12px",
                  fontSize: "13px",
                  color: "#334155",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  background: currentPage === totalPages ? "#f1f5f9" : "white",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: currentPage === totalPages ? "#94a3b8" : "#334155",
                  transition: "all 0.2s ease",
                }}
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  background: currentPage === totalPages ? "#f1f5f9" : "white",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: currentPage === totalPages ? "#94a3b8" : "#334155",
                  transition: "all 0.2s ease",
                }}
              >
                Last
              </button>
            </div>
          </div>
        )}

        {/* FOOTER STATS */}
        
      </Box>
    </div>
  );
};

export default CommonTable;